import { GetServerSideProps, GetServerSidePropsContext } from "next/types";
import { useEffect, useState } from "react";

import { getAnalytics } from "@tm/insurance/analytics";
import { navigationActions } from "@tm/insurance/navigation";
import { getCookieByName } from "@tm/insurance/utils";
import { PARTNER } from "@tm/isomorphic-utils";
import { getLandingPageDetails } from "@tm/server-api-utils";
import { getPartnerOnServerSide } from "@tm/server-utils";
import {
  InsuranceGiLandingPageMainWrapper,
  InsuranceLanding,
  InsuranceLandingPageMainWrapper,
} from "@tm/static/pages";
import { PartnerContextProvider } from "@tm/static/partnerContext";
import { withDeviceProvider } from "@tm/ui-widgets";
import {
  withExperimentTrackingStringProvider,
  withReferrerProvider,
} from "@tm/ui-widgets/hoc";
import {
  withError,
  withGIInsuranceHeader,
  withMMTInsuranceHeader,
} from "@tm/ui/hoc";
import { authenticatedGSSP } from "../../src/utils";
import { AuthenticatedGSSPProps } from "../../src/utils/authenticatedGSSP";

const analytics = getAnalytics();

export function LandingPage({
  data,
  device = "mobile",
  error,
  hasPartnerAuth,
  partner,
  referrer,
  cmp = "",
  experiment = "",
}: AuthenticatedGSSPProps) {
  useEffect(() => {
    // migrate this to server referrer
    const referrer = document.referrer;
    const hostname = referrer ? new URL(referrer).hostname : "";

    if (
      cmp === "" &&
      (hostname === "www.makemytrip.com" || hostname === "www.goibibo.com")
    ) {
      cmp = "secondary_icon";
    }
    sessionStorage.setItem("cmp", cmp);
  }, []);

  const [activeModal, setActiveModal] = useState("");

  const goToMyOrders = () => {
    analytics?.trackLandingEvents({
      actionType: "my_orders_clicked",
    });

    const authCookie =
      getCookieByName("mmt-auth") || getCookieByName("OAUTH-GOIBIBO");

    if (authCookie) {
      navigationActions.navigateToOrderListing();
    } else {
      setActiveModal?.("LOGIN");
    }
  };
  const comp =
    partner === PARTNER.GI
      ? withGIInsuranceHeader(
          <PartnerContextProvider partnerType={PARTNER.GI}>
            <InsuranceLanding setActiveModal={setActiveModal} data={data} />
          </PartnerContextProvider>,
          {
            Wrapper: InsuranceGiLandingPageMainWrapper,
            forceLogin: !hasPartnerAuth,
          },
          "Secure International Travel",
          true,
          goToMyOrders
        )
      : withMMTInsuranceHeader(
          <PartnerContextProvider partnerType={PARTNER.MMT}>
            <InsuranceLanding setActiveModal={setActiveModal} data={data} />
          </PartnerContextProvider>,
          {
            type: hasPartnerAuth ? "dualHeader" : "singleHeader",
            Wrapper: InsuranceLandingPageMainWrapper,
            activeModal: activeModal ? "LOGIN" : undefined,
            onModalChange: (modal) => setActiveModal(modal),
            forceLogin: !hasPartnerAuth,
          },
          "Secure International Travel",
          true,
          goToMyOrders
        );

  return withError(
    withReferrerProvider(
      withDeviceProvider(
        withExperimentTrackingStringProvider(comp, experiment),
        device
      ),
      referrer
    ),
    error,
    "LANDING"
  );
}

export const getServerSideProps: GetServerSideProps = async (
  ctx: GetServerSidePropsContext
) => {
  const { req, query } = ctx;

  const partner = getPartnerOnServerSide(req?.headers);

  const { cmp = "" } = query || {};
  const requestData = { partner, headers: req.headers, cmp, query };

  return authenticatedGSSP(
    ctx,
    () => getLandingPageDetails(requestData),
    partner,
    cmp
  );
};

export default LandingPage;
